import { useUrlParam } from "@opendash/core";
import { Source } from "@opendash/plugin-parse/dist/types-generated";
import { Flex, Tooltip, Typography } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { useMetaTranslate } from "../hooks/useMetaTranslate";
import { SourceMetaTag } from "./SourceMetaTag";

type Props = {
  source: Source;
};

type MetaField = {
  field: string;
  value: string;
};

// to add tranlation to the meta fields use: "sourcemeta.view.meta.label.<displayed-metafield-name>"

export const SourceMetaLabel = observer((props: Props) => {
  const mt = useMetaTranslate();
  const [searchString] = useUrlParam<string>("search", "", "string");
  const [foundMetaFields, setFoundMetaFields] = useState<MetaField[]>([]);

  useEffect(() => {
    setFoundMetaFieldsArray();
  }, [searchString]);

  /**
   * Sets the foundMetaFields-State based on the search string. All MetaFields that match the search string parts will be added to the state.
   * @returns Array of Meta Fields that match the search string parts
   */
  function setFoundMetaFieldsArray() {
    const splittedSearchParts = searchString.split(",").map((s) => s.trim());
    const foundMetaFieldsArray: MetaField[] = [];
    setFoundMetaFields(foundMetaFieldsArray);

    // do not search the meta if the source has no meta data or the search string is empty
    if (!props.source?.meta?.data || !searchString) return;

    // search for each search part in each meta field
    splittedSearchParts.forEach((searchValue) => {
      const metaFields = Object.keys(props.source.meta.data);

      metaFields.forEach((metaField) => {
        const metaValue = props.source.meta.data[metaField];

        // if the meta value is an array, search for each value in the array
        if (metaValue instanceof Array) {
          metaValue.forEach((value) => {
            if (
              value
                .toString()
                .toLowerCase()
                .trim()
                .includes(searchValue.toLowerCase()) &&
              !foundMetaFieldsArray.find((pair) => pair.field === metaField)
            ) {
              foundMetaFieldsArray.push({
                field: metaField,
                value: metaValue.join(", "),
              });
            }
          });
        } else {
          // if its not an array search for the value
          if (
            metaValue
              .toString()
              .toLowerCase()
              .trim()
              .includes(searchValue.toLowerCase()) &&
            !foundMetaFieldsArray.find((pair) => pair.field === metaField)
          ) {
            foundMetaFieldsArray.push({
              field: metaField,
              value: metaValue.toString().trim(),
            });
          }
        }
      });
    });

    // set the found meta fields to the state
    setFoundMetaFields(foundMetaFieldsArray);
  }

  return (
    <Flex justify="space-between">
      <Typography.Text strong>{props.source.name}</Typography.Text>
      <Flex gap="8px">
        {foundMetaFields.map((pair, index) => {
          return (
            <Tooltip key={pair.field + index} title={pair.value}>
              <SourceMetaTag metaField={pair.field} />
            </Tooltip>
          );
        })}
      </Flex>
    </Flex>
  );
});
